import React, { createContext, useState, useEffect, useContext } from 'react';
import { authApi } from '../api/apiService';
import { message } from 'antd';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTimer, setRefreshTimer] = useState(null);

  // Set up token refresh timer
  const setupRefreshTimer = () => {
    // Clear any existing timer
    if (refreshTimer) {
      clearTimeout(refreshTimer);
    }
    
    const tokenExpiration = localStorage.getItem('tokenExpiration');
    if (!tokenExpiration) return;
    
    const expirationTime = parseInt(tokenExpiration);
    const currentTime = new Date().getTime();
    
    // If token is already expired
    if (currentTime >= expirationTime) {
      message.warning('您的会话已过期。请重新登录。', 5);
      logout();
      return;
    }
    
    // Calculate time until token expires (minus 5 minutes for safety)
    const timeUntilExpiry = expirationTime - currentTime - (5 * 60 * 1000);
    
    // Set timer to refresh token 5 minutes before it expires
    const timer = setTimeout(async () => {
      try {
        await refreshToken();
        message.success('您的会话已延长。', 3);
      } catch (err) {
        message.error('无法延长您的会话。请重新登录。', 5);
        logout();
      }
    }, timeUntilExpiry);
    
    setRefreshTimer(timer);
  };

  // Refresh token function
  const refreshToken = async () => {
    try {
      const data = await authApi.refreshToken();
      setCurrentUser(data.user);
      setupRefreshTimer(); // Set up the next refresh
      return true;
    } catch (err) {
      console.error("Token refresh failed:", err);
      return false;
    }
  };

  // Check if user is logged in on initial load
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        if (localStorage.getItem('token')) {
          // Check if token is expired
          const tokenExpiration = localStorage.getItem('tokenExpiration');
          const currentTime = new Date().getTime();
          
          if (tokenExpiration && currentTime >= parseInt(tokenExpiration)) {
            // Try to refresh the token
            const refreshed = await refreshToken();
            if (!refreshed) {
              throw new Error('Session expired');
            }
          } else {
            // Token still valid, get user data
            const userData = await authApi.getCurrentUser();
            setCurrentUser(userData);
            setupRefreshTimer();
          }
        }
      } catch (err) {
        console.error("Auth check failed:", err);
        
        if (err.response && 
            (err.response.status === 401 || err.response.status === 403) && 
            (err.response.data?.error?.includes('expired') || 
             err.message.includes('expired'))) {
          message.error('Your session has expired. Please log in again.', 5);
        }
        
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('tokenExpiration');
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
    
    // Clean up timer on unmount
    return () => {
      if (refreshTimer) {
        clearTimeout(refreshTimer);
      }
    };
  }, []);

  // Login function
  const login = async (username, password) => {
    try {
      setError(null);
      
      // DEVELOPMENT MODE ONLY: Skip actual login for local testing
      // This is only for development and should be removed in production
      if (process.env.NODE_ENV === 'development' && username === 'dev' && password === 'dev') {
        console.log('DEV MODE: Bypassing real login and using mock data');
        const mockUserData = {
          id: 'dev-user-id',
          email: 'dev@example.com',
          role: 'admin',
          balance: 1000,
          username: 'dev',
          created_at: new Date().toISOString()
        };
        
        // Store mock token
        const mockToken = 'dev-token-for-testing';
        localStorage.setItem('token', mockToken);
        localStorage.setItem('refreshToken', 'dev-refresh-token');
        
        // Set expiration to 1 hour from now
        const expirationTime = new Date().getTime() + 3600 * 1000;
        localStorage.setItem('tokenExpiration', expirationTime);
        
        setCurrentUser(mockUserData);
        return true;
      }
      
      // Normal login flow
      const data = await authApi.login(username, password);
      setCurrentUser(data.user);
      setupRefreshTimer();
      return true;
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.error || "Login failed");
      return false;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      setError(null);
      await authApi.logout();
      
      // Clear refresh timer
      if (refreshTimer) {
        clearTimeout(refreshTimer);
        setRefreshTimer(null);
      }
      
      setCurrentUser(null);
      return true;
    } catch (err) {
      setError(err.response?.data?.error || "Logout failed");
      setCurrentUser(null);
      return true; // Return true anyway since we're removing the user from state
    }
  };

  const value = {
    currentUser,
    login,
    logout,
    refreshToken,
    error,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 